import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFeedbackApi, feedbackError, type FeedbackNotification } from './feedbackApi';
import { FeedbackCapture } from './FeedbackCapture';

interface FeedbackContextValue {
  permissions: string[]; ready: boolean; error: string; openCapture: () => void; allows: (permission: string) => boolean;
  notifications: FeedbackNotification[]; refreshNotifications: () => void; readNotification: (id: string) => Promise<void>;
}
const FeedbackContext = createContext<FeedbackContextValue | null>(null);
const unavailable: FeedbackContextValue = { permissions: [], ready: false, error: '', openCapture: () => {}, allows: () => false,
  notifications: [], refreshNotifications: () => {}, readNotification: async () => {} };
export function useFeedback() {
  const context = useContext(FeedbackContext);
  return context ?? unavailable;
}
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth0();
  const api = useFeedbackApi();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [owner, setOwner] = useState<string>();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [capture, setCapture] = useState(false);
  const [notifications, setNotifications] = useState<FeedbackNotification[]>([]);
  const subject = user?.sub;
  const currentSubject = useRef(subject);
  currentSubject.current = isAuthenticated ? subject : undefined;
  const refreshNotifications = useCallback(() => {
    if (isAuthenticated) void api.notifications().then(result => {
      if (currentSubject.current === subject) setNotifications(result);
    }).catch(() => { /* Retry on the next refresh. */ });
  }, [api, isAuthenticated, subject]);
  useEffect(() => {
    let active = true;
    setPermissions([]); setOwner(undefined); setReady(false); setCapture(false); setNotifications([]); setError('');
    if (isAuthenticated) void api.capabilities().then(result => {
      if (active) {
        setOwner(subject); setReady(true);
        if (result.subject === subject) setPermissions(result.permissions);
        else setError('The feedback session changed. Refresh the application.');
      }
    }).catch(e => { if (active) { setOwner(subject); setError(feedbackError(e)); setReady(true); } });
    return () => { active = false; };
  }, [api, isAuthenticated, subject]);
  useEffect(() => {
    if (!ready || owner !== subject) return;
    let active = true;
    const refresh = () => { if (document.visibilityState === 'visible') void api.notifications().then(n => { if (active) setNotifications(n); }).catch(() => {}); };
    refresh(); const timer = window.setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    return () => { active = false; clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [api, owner, ready, subject]);
  const validPermissions = owner === subject ? permissions : [];
  return <FeedbackContext.Provider value={{ permissions: validPermissions, ready: ready && owner === subject, error: owner === subject ? error : '',
    allows: p => validPermissions.includes(p), openCapture: () => setCapture(true), notifications: owner === subject ? notifications : [], refreshNotifications,
    readNotification: async id => { await api.readNotification(id); refreshNotifications(); },
  }}>
    {children}
    {owner === subject && isAuthenticated && <FeedbackCapture key={subject} open={capture} onOpenChange={setCapture} />}
  </FeedbackContext.Provider>;
}
